import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import SectionTitle from '@/components/base/SectionTitle';
import { mediaList, categoryInfo } from '@/mocks/media';
import { useSeo } from '@/hooks/useSeo';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = mediaList.find((m) => m.id === id);
  const [toast, setToast] = useState('');

  useSeo({
    title: item ? `${item.title} - 看片影视介绍与信息` : '影视不存在 - 看片影视大全',
    description: item
      ? `${item.title}（${item.year}）${item.genres.join('、')}：${item.description.slice(0, 70)}`
      : '未找到对应的影视作品。',
    keywords: item ? `${item.title},${item.genres.join(',')},看片影视` : '看片影视',
  });

  const related = useMemo(() => {
    if (!item) return [];
    return mediaList
      .filter((m) => m.id !== item.id)
      .map((m) => {
        const sharedGenres = m.genres.filter((g) => item.genres.includes(g)).length;
        const yearScore = Math.max(0, 3 - Math.abs(m.year - item.year));
        const typeScore = m.type === item.type ? 2 : 0;
        return { item: m, score: sharedGenres * 3 + yearScore + typeScore };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((entry) => entry.item);
  }, [item]);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(''), 2200);
  };

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('链接已复制，快分享给朋友吧');
    } catch {
      showToast('复制失败，请手动复制浏览器地址');
    }
  };

  if (!item) {
    return (
      <div className="max-w-[900px] mx-auto px-4 md:px-8 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-5">
          <i className="ri-file-warning-line text-3xl"></i>
        </div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">影视信息加载失败</h1>
        <p className="text-sm text-foreground-500 mb-6">影视信息加载失败，请稍后重试，或返回首页浏览其他内容。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const info = categoryInfo[item.type];
  const typeLabel = item.type === 'movie' ? '电影' : item.type === 'tv' ? '电视剧' : '综艺';

  return (
    <div className="w-full">
      <div className="relative w-full">
        <div className="absolute inset-0 h-full w-full overflow-hidden">
          <img
            src={item.poster}
            alt=""
            className="w-full h-full object-cover object-top blur-2xl scale-110 opacity-40"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 to-background-50"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${item.type}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {typeLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 font-medium">{item.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-44 md:w-64 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-secondary-100">
                <img
                  src={item.poster}
                  alt={`${item.title}海报`}
                  title={`${item.title} - 看片影视`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-semibold">
                  {typeLabel}
                </span>
                <span className="flex items-center gap-1 text-accent-600 text-sm font-bold">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)}
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-extrabold text-foreground-950 tracking-tight mb-4">
                {item.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-5">
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${item.type}`}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-6 text-sm">
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">上映年份</dt>
                  <dd className="text-foreground-900">{item.year} 年</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">地区</dt>
                  <dd className="text-foreground-900">{item.region}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">时长/集数</dt>
                  <dd className="text-foreground-900">{item.duration}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">导演</dt>
                  <dd className="text-foreground-900">{item.director}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-foreground-500 shrink-0">主要演员</dt>
                  <dd className="text-foreground-900">{item.actors.join(' / ')}</dd>
                </div>
              </dl>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-link"></i>
                  复制链接分享
                </button>
                <Link
                  to={`/category/${item.type}`}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm font-semibold text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-links-line"></i>
                  进入{info.name}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pb-12 md:pb-16">
        <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            内容简介
          </h2>
          <p className="text-sm md:text-base leading-loose text-foreground-700">{item.description}</p>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <SectionTitle title="相关推荐" subtitle="根据类型与年份为你推荐" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {related.map((rel) => (
                <MovieCard key={rel.id} item={rel} />
              ))}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm shadow-lg animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}